import { FieldControllerType } from '@gemini/lux-ds-pms';
import { StaticValues } from './enums/StaticValues.enum';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import {
  CustomFieldToSend,
  Patient,
  usePatientStore,
} from '@store/patientStore';
import {
  CustomFields,
  FooterCtaDTO,
  GeneralConfigDTO,
  InsuranceDTO,
} from '@utils/interfaces';
import { TFunction } from 'i18next';
import MD5 from 'crypto-js/md5';
import SHA256 from 'crypto-js/sha256';
import encHex from 'crypto-js/enc-hex';

export type GenericObj = { [key: string]: any };

export function mergeObjects<T = GenericObj>(
  obj1: GenericObj,
  obj2: GenericObj,
  copyFromObj2 = true,
) {
  const result = { ...obj1 }; // Crea una copia superficiale di obj1

  for (const key in obj2) {
    if (Object.prototype.hasOwnProperty.call(obj2, key)) {
      if (Object.prototype.hasOwnProperty.call(result, key)) {
        // Se entrambe le proprietà sono oggetti, unisci ricorsivamente
        if (
          typeof result[key] === 'object' &&
          !Array.isArray(result[key]) &&
          typeof obj2[key] === 'object' &&
          !Array.isArray(obj2[key])
        ) {
          result[key] = mergeObjects(result[key], obj2[key]);
        } else {
          // Altrimenti, sovrascrivi la proprietà con quella di obj2
          result[key] = obj2[key];
        }
      } else if (copyFromObj2) {
        // Se la proprietà non esiste in result, aggiungila da obj2
        result[key] = obj2[key];
      }
    }
  }

  //obj1 = {"a": "a", "b": {"b1": "b1", "b2": "b2"}, "c": "c"}
  //obj2 = {"a": "1". "b": {"b1": "b3"}}
  //result = {"a": "1", "b": {"b1": "b3", "b2": "b2"}, "c": "c"}

  return result as T;
}

export const buildExternalLinks = (
  config: GeneralConfigDTO,
): FooterCtaDTO[] => {
  const custom = config.customization;
  const res = [];
  if (custom?.customLink1Label && custom?.customLink1URL) {
    res.push({
      label: custom.customLink1Label,
      url: custom.customLink1URL,
    });
  }
  if (custom?.customLink2Label && custom?.customLink2URL) {
    res.push({
      label: custom.customLink2Label,
      url: custom.customLink2URL,
    });
  }
  if (custom?.customLink3Label && custom?.customLink3URL) {
    res.push({
      label: custom.customLink3Label,
      url: custom.customLink3URL,
    });
  }
  return res;
};

export const excludeNull = (obj: GenericObj) => {
  return Object.keys(obj).reduce((prev, key) => {
    if (obj[key] || typeof obj[key] === 'number') {
      prev[key] = obj[key].toString();
    }

    return prev;
  }, {} as GenericObj);
};

// Function to check if a value exists in the enum
export function isValueInEnum(value: string, enumObject: GenericObj) {
  return Object.values(enumObject).includes(value);
}

// Function to get insurance name from options lists matching id
export const getInsuranceName = (
  insurances?: InsuranceDTO,
  id?: string,
): string | undefined => {
  return insurances && id
    ? insurances?.find(insurance => insurance?.insuranceId.toString() === id)
        ?.name
    : undefined;
};

export function resetApp(keepECP = false) {
  if (!keepECP) {
    useOnlineBookingStore.getState().reset();
  }

  usePatientStore.getState().reset();
  sessionStorage.clear();
}

export const mergeCustomFields = (
  storeCustomFieldValues: GenericObj,
  formCustomFieldsChildren: CustomFields['children'],
): CustomFieldToSend => {
  // console.log('#store', storeCustomFieldValues, formCustomFieldsChildren);
  const toReturn = formCustomFieldsChildren
    .map((fc, i) => ({
      id: fc.id,
      question: fc?.labelTextOnly || fc.label,
      answer:
        fc.status !== StaticValues.FIELD_STATUS_HIDDEN
          ? storeCustomFieldValues['field' + (i + 1)]?.toString()
          : undefined,
    }))
    .filter(c => typeof c.answer === 'boolean' || typeof c.answer === 'string');

  return toReturn;
};

export const cloneObject = <T>(values: T) =>
  JSON.parse(JSON.stringify(values)) as T;

export const generateSortedPatientsList = (
  patients: Array<Patient | undefined>,
  t?: TFunction<'translation', undefined, 'translation'>,
) => {
  const tabs = patients.map((p, i) => ({
    content: t
      ? p?.patientDetails?.firstName
        ? p.patientDetails.firstName + ' ' + p.patientDetails.lastName
        : t('detailsSection.newPatient')
      : '',
    order: p?.order,
    tabKey: i,
    patient: p,
  }));

  // Filtra solo quelli che hanno 'order' definito
  const filteredTabs = tabs.filter(t => typeof t.order !== 'undefined');

  const sortedTabs = filteredTabs
    .sort((a, b) => {
      if (a?.order && b?.order) return a.order - b.order;
      if (a?.order) return 1;
      if (b?.order) return -1;
      return 0;
    }) // Ordina direttamente in base a 'order'
    .map((t, i) => ({
      content: t.content,
      index: i, // Utilizza l'indice per mettere in ordine le tab
      tabKey: t.tabKey, // Usa l'indice originale come 'key'
      patient: t.patient, //Passo il paziente
    }));

  return sortedTabs;
};

export const matchPatientWithSlotIds = (
  patientSlotIds: Array<string>,
  slotIds: Array<string>,
) => {
  // return slotIds.every(
  //   (value, index) =>
  //     value === patient?.scheduleExam?.slotId.internalSlots[index].slotId,
  // );
  if (patientSlotIds.length !== slotIds.length) {
    return false;
  }

  let toReturn = true;
  for (const slotId of patientSlotIds) {
    if (!slotIds.includes(slotId)) {
      toReturn = false;
      break;
    }
  }
  return toReturn;
};

export function deepEqual(obj1: GenericObj, obj2: GenericObj) {
  // Se entrambi sono lo stesso oggetto (stessa referenza)
  if (obj1 === obj2) return true;

  // Controlla se sono entrambi oggetti (esclusi null o undefined)
  // Controlla se sono entrambi oggetti validi (non null)
  if (
    typeof obj1 !== 'object' ||
    obj1 === null ||
    typeof obj2 !== 'object' ||
    obj2 === null
  ) {
    return false;
  }

  // Ottieni le chiavi di entrambi gli oggetti
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Controlla se hanno lo stesso numero di chiavi
  if (keys1.length !== keys2.length) return false;

  // Confronta ogni chiave e valore in modo ricorsivo
  for (const key of keys1) {
    // Controlla se l'altra chiave esiste in obj2
    if (!keys2.includes(key)) return false;

    const val1 = obj1[key];
    const val2 = obj2[key];

    // Se i valori sono funzioni, confrontale come stringhe
    if (typeof val1 === 'function' && typeof val2 === 'function') {
      if (val1.toString() !== val2.toString()) {
        return false;
      }
    }
    // Se non sono uguali ricorsivamente o non sono funzioni
    else if (!deepEqual(val1, val2)) {
      return false;
    }
  }

  // Se tutto corrisponde, gli oggetti sono uguali
  return true;
}

export const handleOnEnterKeyPress = (
  event: {
    key: string;
    stopPropagation: () => void;
    preventDefault: () => void;
  },
  action: () => void,
  shouldPreventDefault: boolean = false,
  stopPropagation: boolean = false,
) => {
  if (event.key === 'Enter' || event.key === ' ') {
    action?.();
    if (!action) {
      console.warn('Invalid action');
    }
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (shouldPreventDefault) {
      event.preventDefault();
    }
  }
};

export const convertStringToFieldControllerType = (value: string) => {
  switch (value) {
    case 'RADIO':
      return FieldControllerType.RADIO;
    case 'TOGGLE':
      return FieldControllerType.TOGGLE;
    default:
      return FieldControllerType.TEXT;
  }
};

//TODO DA RIVEDERE, E' MOMENTANEO
// export function getCurrectUrl(type: string, devEnv: string) {
//   switch (type) {
//     case 'TAB':
//       if (devEnv === 'stg') {
//         return 'https://stg-gmnpms-onlinebooking.luxgroup.net';
//       } else if (devEnv === 'uat') {
//         return 'https://uat.visionx.solutions';
//       } else {
//         return 'https://dev-tab-onlinebooking.luxgroup.net';
//       }
//     default:
//       if (devEnv === 'stg') {
//         return 'https://stg-gmnpms-onlinebooking.luxgroup.net';
//       } else if (devEnv === 'uat') {
//         return 'https://uat.visionx.solutions';
//       } else {
//         return 'https://dev-gmnpms-onlinebooking.luxgroup.net';
//       }
//   }
// }

/**
 *  Set text to uppercase if brandName exists
 */
export const conditionallyApplyUpperCase = (
  text: string,
  brandName: string | undefined,
) => (brandName ? text.toUpperCase() : text);

export const getMD5 = (input: string): string => {
  return MD5(input).toString(encHex);
};

export const getSHA256 = (input: string): string => {
  return SHA256(input).toString(encHex);
};
